// Configuration for the exported PDF report. The customizer is plain UI; this
// module is the single source of truth for the report's sections and the helpers
// the PDF document + UI both read, so they never drift.

// The report is organized as a few major parts, each holding the pages that
// belong to it. Every entry is one PDF page; the parent rows are the major
// checkboxes in the customizer, and turning one off drops every page beneath it.
//
// The default report is the short one a client can actually read: what it
// costs, who gets it, person by person, and how the options compare. The
// workforce charts are a real part of the product, but they are not what a
// plan proposal is for, so they ship switched off and are one checkbox away.
export const REPORT_SECTIONS = [
  { key: 'executiveSummary', label: 'Executive summary' },
  { key: 'proposalSummary', label: 'Headline allocation and recipients', parent: 'executiveSummary' },

  { key: 'costAnalysis', label: 'Cost analysis' },
  { key: 'proposalAlternatives', label: 'Comparing the options', parent: 'costAnalysis' },
  { key: 'contributions', label: 'Contributions in detail', parent: 'costAnalysis' },
  { key: 'proposalProjection', label: 'Cash-balance account outlook', parent: 'costAnalysis' },
  // Temporarily disabled for client-facing reports.
  // { key: 'complianceScreens', label: 'Required-testing check', parent: 'costAnalysis' },
  { key: 'cashBalance', label: 'Cash-balance detail', parent: 'costAnalysis' },
  { key: 'limits', label: 'Assumptions, limits, and methodology', parent: 'costAnalysis' },

  { key: 'employeeImpact', label: 'Employee impact' },
  { key: 'proposalContributions', label: 'What each employee gets', parent: 'employeeImpact' },

  // `defaultOff` travels with the section rather than living in a global
  // defaults map: the CFO brief has its own section named `charts`, and a
  // global default would silently reach across and switch that off too.
  { key: 'charts', label: 'Workforce charts (off by default)', parent: 'employeeImpact', defaultOff: true },
  { key: 'engagement', label: 'Who is participating', parent: 'charts' },
  { key: 'distributions', label: 'How contributions vary', parent: 'charts' },
  { key: 'behavior', label: 'Savings behavior', parent: 'charts' },
  { key: 'demographics', label: 'Age and gender', parent: 'charts' },
  { key: 'tenure', label: 'Tenure and loans', parent: 'charts' },
  { key: 'turnover', label: 'Turnover', parent: 'charts' },
  { key: 'custom', label: 'Your saved charts', parent: 'charts' },
]

export const DEFAULT_REPORT_CONFIG = {
  schemaVersion: 2,
  title: '',
  firmName: '',
  clientName: '',
  preparedBy: '',
  // A custom logo (data URL) and debrand mode let a firm make the report their
  // own. Debranding swaps Retira's mark/name for the firm's everywhere it leads –
  // a small "retira.app" attribution is the one mark that always stays.
  logoDataUrl: null,
  // The downscaled source image + (zoom, center) crop, kept so the logo can be
  // reopened and re-cropped later. logoDataUrl is the rendered square the PDF uses.
  logoSource: null,
  logoCrop: null,
  debrand: false,
  // Explicit user choices only; a section the user has not touched follows its
  // registry entry's own default (see `defaultOff`).
  sections: {},
  // Custom render order (section keys). Empty = the default order.
  sectionOrder: [],
  // Presentation assumptions for the proposal pages. Null means inherit the
  // applied proposal input (when present), then fall back to the proposal
  // report defaults. Funding figures stay on the plan proposal because they
  // must be supplied by the enrolled actuary, not invented at export time.
  proposal: {
    marginalTaxRatePercent: null,
    projectionYears: null,
    annualContributionGrowthPercent: null,
    interestCreditAssumptionPercent: null,
  },
}

export const DEFAULT_REPORT_TITLE = 'What the plan costs, who benefits, and how the options compare.'

// The brand is written lowercase ONLY where it sits right next to the logo (the
// cover and page headers), to match the lowercase-r mark. Everywhere else – the
// footer credit, document metadata, body copy – it is the capitalized form.
export const BRAND_WORDMARK = 'retira.app'
// The attribution that remains on every report even when fully debranded.
export const REQUIRED_ATTRIBUTION = 'Retira.app'

export const DEFAULT_SECTION_ORDER = REPORT_SECTIONS.map((section) => section.key)

// The major parts, in their default order, and the pages inside each.
export const REPORT_SECTION_GROUPS = REPORT_SECTIONS
  .filter((section) => !section.parent)
  .map((section) => section.key)

export function sectionChildren(key, registry = REPORT_SECTIONS) {
  return registry.filter((section) => section.parent === key).map((section) => section.key)
}

function descendants(key, registry = REPORT_SECTIONS) {
  return sectionChildren(key, registry).flatMap((child) => [child, ...descendants(child, registry)])
}

function migrateLegacySections(sections = {}) {
  const migrated = { ...(sections || {}) }
  if (migrated.atAGlance === false) {
    for (const key of ['proposalSummary', 'proposalAlternatives', 'proposalContributions', 'contributions', 'proposalProjection']) {
      migrated[key] = false
    }
  }
  if (migrated.compliance === false) {
    migrated.cashBalance = false
    migrated.limits = false
  }
  delete migrated.atAGlance
  delete migrated.compliance
  return migrated
}

// A section is shown unless the user turned it off. Where the user has made no
// choice, the section's own registry entry decides: `defaultOff` sections stay
// out until asked for, everything else is in. Pass the registry the key belongs
// to; the report's own is the default, and the CFO brief passes its own (the two
// share a `charts` key that means different things).
export function isSectionEnabled(config, key, registry = REPORT_SECTIONS) {
  const choice = migrateLegacySections(config?.sections)[key]
  if (choice !== undefined) return choice !== false
  return registry.find((section) => section.key === key)?.defaultOff !== true
}

// What actually renders: a page is visible only when it and every part above it
// are included, so unchecking a major part drops everything beneath it without
// the individual pages having to be unchecked too (they stay checked, ready for
// when the part is turned back on).
export function isSectionVisible(config, key, registry = REPORT_SECTIONS) {
  let current = registry.find((section) => section.key === key)
  if (!current) return isSectionEnabled(config, key, registry)
  const seen = new Set()
  while (current) {
    if (seen.has(current.key)) break
    seen.add(current.key)
    if (!isSectionEnabled(config, current.key, registry)) return false
    current = current.parent ? registry.find((section) => section.key === current.parent) : null
  }
  return true
}

// The section keys in the order the report should render them. Honors a saved
// custom order, but always self-heals: unknown keys are dropped and any section
// missing from a saved order is appended so a new section can never disappear.
export function resolveSectionOrder(config) {
  const saved = Array.isArray(config?.sectionOrder) ? config.sectionOrder : []
  const known = new Set(DEFAULT_SECTION_ORDER)
  const ordered = saved.filter((key) => known.has(key))
  const seen = new Set(ordered)
  for (const key of DEFAULT_SECTION_ORDER) if (!seen.has(key)) ordered.push(key)
  return ordered
}

// Only the major parts are reorderable: the pages inside one always keep the
// order they read best in. An order saved before the report was grouped holds
// page keys only, so it simply falls back to the default part order.
export function resolveGroupOrder(config) {
  const groups = new Set(REPORT_SECTION_GROUPS)
  const saved = (Array.isArray(config?.sectionOrder) ? config.sectionOrder : []).filter((key) => groups.has(key))
  const ordered = [...new Set(saved)]
  const seen = new Set(ordered)
  for (const key of REPORT_SECTION_GROUPS) if (!seen.has(key)) ordered.push(key)
  return ordered
}

// The flat page sequence the document renders: each major part in order, then
// the pages inside it. A part with no pages of its own is a page itself.
export function resolvePageOrder(config) {
  const leaves = (key) => {
    const children = sectionChildren(key)
    return children.length ? children.flatMap(leaves) : [key]
  }
  return resolveGroupOrder(config).flatMap(leaves)
}

// Reorder the major parts, keeping each part's pages with it, and return the
// full key order to save (so the saved order stays a complete, valid list).
export function moveSectionGroup(config, fromKey, toKey) {
  const moved = moveInOrder(resolveGroupOrder(config), fromKey, toKey)
  return moved.flatMap((key) => [key, ...descendants(key)])
}

// Move one section so it lands just before the section it was dropped on. Returns
// a new array; a no-op (same key, or an unknown target) returns the input order.
export function moveInOrder(order, fromKey, toKey) {
  if (fromKey === toKey || !order.includes(fromKey)) return order
  const without = order.filter((key) => key !== fromKey)
  const targetIndex = without.indexOf(toKey)
  if (targetIndex === -1) return order
  without.splice(targetIndex, 0, fromKey)
  return without
}

export function resolveReportConfig(config = {}) {
  return {
    ...DEFAULT_REPORT_CONFIG,
    ...config,
    schemaVersion: 2,
    sections: { ...DEFAULT_REPORT_CONFIG.sections, ...migrateLegacySections(config.sections) },
    proposal: { ...DEFAULT_REPORT_CONFIG.proposal, ...(config.proposal || {}) },
  }
}

// The cover/meta strings the document renders, falling back to sensible defaults
// when the user has not customized them.
export function resolveReportMeta(config, { planName } = {}) {
  const resolved = resolveReportConfig(config)
  return {
    title: resolved.title?.trim() || DEFAULT_REPORT_TITLE,
    firmName: resolved.firmName?.trim() || '',
    clientName: resolved.clientName?.trim() || planName || '',
    preparedBy: resolved.preparedBy?.trim() || '',
  }
}

// A saveable "brand profile": the firm's logo, name, and preferred report
// settings, portable as a file (and, optionally, to the cloud).
const PROFILE_KEYS = ['schemaVersion', 'title', 'firmName', 'clientName', 'preparedBy', 'logoDataUrl', 'logoSource', 'logoCrop', 'debrand', 'sections', 'sectionOrder', 'proposal']

export function serializeReportProfile(config) {
  const resolved = resolveReportConfig(config)
  const profile = Object.fromEntries(PROFILE_KEYS.map((key) => [key, resolved[key]]))
  return JSON.stringify({ type: 'retira-report-profile', version: 2, profile }, null, 2)
}

// Parse + sanitize a profile file into a config patch. Unknown keys are ignored
// and every field is coerced to its expected type, so a hand-edited or foreign
// file can never inject unexpected shape into the store.
export function parseReportProfile(json) {
  const data = typeof json === 'string' ? JSON.parse(json) : json
  const profile = data && typeof data === 'object' ? (data.profile || data) : null
  if (!profile || typeof profile !== 'object') throw new Error('Not a valid report profile file.')
  const str = (value, maxLength = 200) => (typeof value === 'string' ? value.slice(0, maxLength) : '')
  const imageOrNull = (value) => (typeof value === 'string' && value.startsWith('data:image/') ? value : null)
  const logo = imageOrNull(profile.logoDataUrl)
  const logoSource = imageOrNull(profile.logoSource)
  const num = (value, fallback) => {
    if (value === null || value === undefined || value === '') return fallback
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  const logoCrop = profile.logoCrop && typeof profile.logoCrop === 'object' && !Array.isArray(profile.logoCrop)
    ? { zoom: num(profile.logoCrop.zoom, 1), cx: num(profile.logoCrop.cx, 0.5), cy: num(profile.logoCrop.cy, 0.5) }
    : null
  const sections = profile.sections && typeof profile.sections === 'object' && !Array.isArray(profile.sections)
    ? Object.fromEntries(Object.entries(profile.sections).map(([key, value]) => [key, value !== false]))
    : {}
  const legacyOrder = Array.isArray(profile.sectionOrder)
    && profile.sectionOrder.some((key) => key === 'atAGlance' || key === 'compliance')
  const legacyProfile = data?.version === 1 || profile.schemaVersion === 1 || legacyOrder
  const sectionOrder = Array.isArray(profile.sectionOrder) && !legacyProfile
    ? profile.sectionOrder.filter((key) => DEFAULT_SECTION_ORDER.includes(key))
    : []
  const rawProposal = profile.proposal && typeof profile.proposal === 'object' && !Array.isArray(profile.proposal)
    ? profile.proposal : {}
  const nullableBounded = (value, minimum, maximum, { integer = false } = {}) => {
    if (value === '' || value === null || value === undefined) return null
    const parsed = Number(value)
    if (!Number.isFinite(parsed)) return null
    const bounded = Math.min(maximum, Math.max(minimum, parsed))
    return integer ? Math.round(bounded) : bounded
  }
  const proposal = {
    marginalTaxRatePercent: nullableBounded(rawProposal.marginalTaxRatePercent, 0, 60),
    projectionYears: nullableBounded(rawProposal.projectionYears, 1, 30, { integer: true }),
    annualContributionGrowthPercent: nullableBounded(rawProposal.annualContributionGrowthPercent, 0, 15),
    interestCreditAssumptionPercent: nullableBounded(rawProposal.interestCreditAssumptionPercent, 0, 15),
  }
  return resolveReportConfig({
    schemaVersion: 2,
    title: str(profile.title, 240),
    firmName: str(profile.firmName, 160),
    clientName: str(profile.clientName, 160),
    preparedBy: str(profile.preparedBy, 160),
    logoDataUrl: logo,
    logoSource,
    logoCrop,
    debrand: Boolean(profile.debrand),
    sections: migrateLegacySections(sections),
    sectionOrder,
    proposal,
  })
}

// The logo + lead brand name the document leads with. Debranding takes effect as
// soon as the firm supplies *something* to lead with – a firm name, a logo, or
// both – so an uploaded logo is honored even when the name field is left blank.
// Otherwise the lead is the lowercase "retira.app" wordmark (the logo is a
// lowercase r, so the text matches it). The attribution that always remains is
// returned separately.
export function resolveReportBrand(config, retiraLogoUrl) {
  const resolved = resolveReportConfig(config)
  const firmName = resolved.firmName?.trim()
  const firmLogo = resolved.logoDataUrl || null
  const debranded = Boolean(resolved.debrand && (firmName || firmLogo))
  return {
    // The firm's own logo when they uploaded one; otherwise the retira.app logo.
    logoUrl: debranded ? (firmLogo || retiraLogoUrl) : retiraLogoUrl,
    // `name` sits next to the logo → the firm name when debranded (may be empty
    // if they branded by logo alone), else the lowercase wordmark.
    name: debranded ? firmName : BRAND_WORDMARK,
    debranded,
    // `attribution` is used away from the logo (footer, metadata) → capitalized.
    attribution: REQUIRED_ATTRIBUTION,
  }
}
